import React, { useEffect, useState } from 'react';
import { MessageSquare, Edit, Check, Trash2, Calendar, User as UserIcon, Image as ImageIcon } from 'lucide-react';
import { FeedbackService } from '../services/feedbackService';
import { Modal, ModalBody, ModalFooter, Dropdown, Button, ConfirmDialog, Toggle, Badge } from './ui';
import FilePreviewModalSimple from './FileUpload/FilePreviewModalSimple';

const FeedbackDetailsModal = ({ feedback, isOpen, onClose, initialEditMode = false, onFeedbackUpdated }) => {
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [formData, setFormData] = useState({
    request: '',
    details: '',
    type: '',
    priority: '',
    status: 'open'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  const typeOptions = [
    { value: 'Bug', label: 'Bug' },
    { value: 'New Feature', label: 'New Feature' },
    { value: 'UI Issue', label: 'UI Issue' },
  ];

  const priorityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
  ];

  // Initialize form data when feedback changes
  useEffect(() => {
    if (feedback) {
      setFormData({
        request: feedback.request || '',
        details: feedback.details || '',
        type: feedback.type || '',
        priority: feedback.priority || '',
        status: feedback.status || 'open'
      });
    }
  }, [feedback]);

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setErrors({});
    // Reset form data to original feedback data
    if (feedback) {
      setFormData({
        request: feedback.request || '',
        details: feedback.details || '',
        type: feedback.type || '',
        priority: feedback.priority || '',
        completed: feedback.completed || false
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.request.trim()) {
      newErrors.request = 'Request is required';
    }

    if (!formData.type) {
      newErrors.type = 'Type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const updatedFeedback = await FeedbackService.updateFeedback(feedback.id, {
        request: formData.request,
        details: formData.details || null,
        type: formData.type,
        priority: formData.priority,
        status: formData.status,
        _oldStatus: feedback.status
      });

      setIsEditMode(false);
      setErrors({});

      if (onFeedbackUpdated) {
        onFeedbackUpdated(updatedFeedback);
      }
    } catch (error) {
      console.error('Error updating feedback:', error);
      setErrors({ submit: 'Failed to update feedback. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDelete = () => {
    if (!feedback?.id) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!feedback?.id) return;

    setLoading(true);
    try {
      await FeedbackService.deleteFeedback(feedback.id);

      // Close both modals and trigger refresh
      setShowDeleteConfirm(false);
      if (onFeedbackUpdated) {
        onFeedbackUpdated(null); // Signal that feedback was deleted
      }
      onClose();
    } catch (error) {
      console.error('Error deleting feedback:', error);
      setErrors({ submit: 'Failed to delete feedback. Please try again.' });
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'Bug':
        return 'red';
      case 'New Feature':
        return 'blue';
      case 'UI Issue':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'red';
      case 'Medium':
        return 'orange';
      case 'Low':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'open':
        return 'gray';
      case 'in_progress':
        return 'blue';
      case 'completed':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open':
        return 'Open';
      case 'in_progress':
        return 'Working';
      case 'completed':
        return 'Completed';
      default:
        return 'Unknown';
    }
  };

  if (!feedback) return null;

  const screenshots = feedback.screenshot_urls || [];

  return (
    <>
    <Modal trackAs="FeedbackDetailsModal" isOpen={isOpen} onClose={onClose} size="lg">
      {/* Custom Header with Icon and Title */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--cta-blue)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MessageSquare size={28} color="white" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-50)',
              }}
            >
              Feedback Details
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {isEditMode
                ? `${formData.type || 'Type'} • ${formData.priority || 'Priority'} Priority`
                : `${feedback.type || 'Type'} • ${feedback.priority || 'Priority'} Priority`}
            </p>
          </div>
        </div>
      </div>

      <ModalBody>
        {/* Error message */}
        {errors.submit && (
          <div
            style={{
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid var(--danger)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--danger)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {errors.submit}
          </div>
        )}

        {/* Two column layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
          {/* Left Column: Request Details */}
          <div>
            <h2
              style={{
                margin: 0,
                marginBottom: 'var(--space-4)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-200)',
              }}
            >
              Request Details
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {/* Request */}
              {isEditMode ? (
                <div>
                  <label
                    htmlFor="request"
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-300)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                    }}
                  >
                    Request <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <textarea
                    id="request"
                    name="request"
                    value={formData.request}
                    onChange={handleChange}
                    rows={Math.max(3, Math.ceil(formData.request.length / 60))}
                    placeholder="Describe your request or issue"
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: errors.request ? '1px solid var(--danger)' : '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--gray-200)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color var(--transition-fast)',
                    }}
                    onFocus={(e) => {
                      if (!errors.request) {
                        e.target.style.borderColor = 'var(--cta-blue)';
                      }
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      if (!errors.request) {
                        e.target.style.borderColor = 'var(--gray-600)';
                      }
                    }}
                  />
                  {errors.request && (
                    <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--danger)' }}>
                      {errors.request}
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Request
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', lineHeight: 'var(--leading-relaxed)' }}>
                    {feedback.request || 'Not provided'}
                  </div>
                </div>
              )}

              {/* Details */}
              {(feedback.details || isEditMode) && (
                isEditMode ? (
                  <div>
                    <label
                      htmlFor="details"
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        color: 'var(--gray-300)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                      }}
                    >
                      Details / Loom Link
                    </label>
                    <textarea
                      id="details"
                      name="details"
                      value={formData.details}
                      onChange={handleChange}
                      rows={Math.max(4, Math.ceil(formData.details.length / 60))}
                      placeholder="Additional details or paste a Loom link"
                      style={{
                        width: '100%',
                        padding: 'var(--space-3)',
                        backgroundColor: 'var(--dark-elevated)',
                        border: '1px solid var(--gray-600)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        transition: 'border-color var(--transition-fast)',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--cta-blue)';
                        e.target.style.outline = 'none';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--gray-600)';
                      }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Details / Loom Link
                    </label>
                    <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {feedback.details || 'No details provided'}
                    </div>
                  </div>
                )
              )}

              {/* Screenshots */}
              {screenshots.length > 0 && !isEditMode && (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <ImageIcon size={14} />
                    Screenshots ({screenshots.length})
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                    {screenshots.map((screenshot, index) => (
                      <div
                        key={screenshot.id || index}
                        onClick={() => handleImageClick(screenshot)}
                        style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: 'var(--radius-md)',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          border: '1px solid var(--gray-600)',
                          transition: 'all var(--transition-fast)',
                          position: 'relative',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                          e.currentTarget.style.borderColor = 'var(--cta-blue)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.borderColor = 'var(--gray-600)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <img
                          src={screenshot.url}
                          alt={screenshot.filename || `Screenshot ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div style={{
                          display: 'none',
                          width: '100%',
                          height: '100%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'var(--gray-700)',
                          color: 'var(--gray-400)',
                        }}>
                          <ImageIcon size={24} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Metadata */}
          <div>
            <h2
              style={{
                margin: 0,
                marginBottom: 'var(--space-4)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-200)',
              }}
            >
              Details & Status
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {/* Type */}
              {isEditMode ? (
                <Dropdown
                  label="Type"
                  options={typeOptions}
                  value={formData.type}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, type: value }));
                    if (errors.type) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.type;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Select type"
                  error={errors.type}
                  required
                />
              ) : (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Type
                  </label>
                  <Badge color={getTypeBadgeColor(feedback.type)}>
                    {feedback.type || 'Not specified'}
                  </Badge>
                </div>
              )}

              {/* Priority */}
              {isEditMode ? (
                <Dropdown
                  label="Priority"
                  options={priorityOptions}
                  value={formData.priority}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, priority: value }));
                  }}
                  placeholder="Select priority"
                />
              ) : (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Priority
                  </label>
                  <Badge color={getPriorityBadgeColor(feedback.priority)}>
                    {feedback.priority || 'Not specified'}
                  </Badge>
                </div>
              )}

              {/* Status */}
              {isEditMode ? (
                <Dropdown
                  label="Status"
                  options={[
                    { value: 'open', label: 'Open' },
                    { value: 'in_progress', label: 'Working' },
                    { value: 'completed', label: 'Completed' }
                  ]}
                  value={formData.status}
                  onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
                  placeholder="Select status"
                />
              ) : (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Status
                  </label>
                  <Badge color={getStatusBadgeColor(feedback.status)}>
                    {getStatusLabel(feedback.status)}
                  </Badge>
                </div>
              )}

              {/* Created Date */}
              <div
                style={{
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--dark-surface)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-lg)',
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <Calendar size={14} />
                  Created
                </label>
                <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                  {feedback.created_at ? new Date(feedback.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'Not available'}
                </div>
              </div>

              {/* Created By */}
              {feedback.employees?.name && (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <UserIcon size={14} />
                    Created By
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                    {feedback.employees.name}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalBody>

      {/* Sticky Footer with Actions */}
      <ModalFooter>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {isEditMode ? (
            <>
              {/* Left: Delete Button */}
              <Button variant="danger" size="md" onClick={handleDelete} disabled={loading} icon={<Trash2 size={16} />}>
                Delete Feedback
              </Button>

              {/* Right: Cancel & Save */}
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <Button variant="ghost" size="md" onClick={handleCancelEdit} disabled={loading}>
                  Cancel
                </Button>
                <Button variant="primary" size="md" onClick={handleSave} loading={loading} icon={<Check size={16} />}>
                  Save Changes
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Empty left side for balance */}
              <div></div>

              {/* Right: Edit Button */}
              <Button variant="secondary" size="md" onClick={handleEdit} icon={<Edit size={16} />}>
                Edit Feedback
              </Button>
            </>
          )}
        </div>
      </ModalFooter>
    </Modal>

    {/* Delete Confirmation Dialog */}
    <ConfirmDialog
      isOpen={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      onConfirm={confirmDelete}
      title="Delete Feedback"
      message={`Are you sure you want to delete this feedback? This action cannot be undone.`}
      confirmText="Delete Feedback"
      cancelText="Cancel"
      variant="danger"
      loading={loading}
    />

    {/* Image Preview Modal */}
    <FilePreviewModalSimple
      file={selectedImage}
      isOpen={showImageModal}
      onClose={handleCloseImageModal}
    />
    </>
  );
};

export default FeedbackDetailsModal;
